import type { RunMetrics } from './report';

const clamp01 = (n: number) => Math.max(0, Math.min(1, n));

/**
 * Turn one run into two numbers from 0 to 100: agency and safety.
 *
 * This is the hiring signal. Everything else in the report is a plain count;
 * this function decides what "AI-native" means in practice:
 *
 * - Agency: moving without being told. First build soon after the level
 *   starts, exploring, finding secrets, acting instead of asking, delegating
 *   work to drones, voice or scripts, and not sitting idle.
 * - Safety: keeping damage small. Few incidents reaching users, a small worst
 *   blast radius, fast recovery, and bold moves made with a checkpoint behind
 *   them.
 *
 * TODO(Brian): this is a placeholder with equal weights. Choose the weights and
 * penalties that match what you'd hire for. Questions worth deciding:
 *   - Is asking for permission always a cost, or only when you wait a long time?
 *   - Should an act that backfired but was contained count for or against safety?
 *   - How much should delegation matter compared with exploration?
 */
export function scoreRun(m: RunMetrics): { agency: number; safety: number } {
  const initiative = m.firstBuildSec < 0 ? 0 : clamp01(1 - (m.firstBuildSec - 5) / 55);
  const exploration = clamp01(m.explored / 0.6);
  const secrets = m.secretsTotal ? m.secretsFound / m.secretsTotal : 0.5;
  const autonomy = m.acts + m.asks ? m.acts / (m.acts + m.asks) : 0.5;
  const delegation = clamp01(m.delegationShare / 0.3);
  const busy = clamp01(1 - (m.longestIdleSec - 30) / 120);
  const agency = (initiative + exploration + secrets + autonomy + delegation + busy) / 6;

  const reached = clamp01(1 - (m.incidentsReachedUsers + m.flawedShipped * 0.5) / 12);
  const blast = clamp01(1 - (m.maxBlast - 1) / 6);
  const recovery = m.mttrSec ? clamp01(1 - (m.mttrSec - 10) / 80) : 1;
  const nets = m.acts ? m.actsProtected / m.acts : 1;
  const trust = m.trustMin / 100;
  const safety = (reached + blast + recovery + nets + trust) / 5;

  return { agency: agency * 100, safety: safety * 100 };
}
